
import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { SentimentResults } from "./sentimentService";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

interface SentimentChartsProps {
  results: SentimentResults;
}

export const SentimentCharts: React.FC<SentimentChartsProps> = ({ results }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Top Keywords */}
      <Card className="bg-secondary/50 border-2 border-primary">
        <CardHeader>
          <CardTitle className="text-white">Top Keywords</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px] pb-2">
          <ChartContainer config={{
            positive: { color: '#4ade80' },
            negative: { color: '#f87171' },
            neutral: { color: '#facc15' }
          }} className="w-full h-full">
            <BarChart 
              data={results.keywords?.map(k => ({
                name: k.word,
                value: k.count,
                sentiment: k.sentiment > 0.3 ? 'positive' : k.sentiment < -0.3 ? 'negative' : 'neutral'
              }))} 
              margin={{ top: 10, right: 10, left: 30, bottom: 50 }}
              barSize={15}
              barGap={2}
            >
              <XAxis 
                dataKey="name" 
                stroke="#fff" 
                tick={{ fontSize: 9, fill: '#fff' }} 
                angle={-45}
                textAnchor="end"
                height={45}
                interval={0}
              />
              <YAxis 
                stroke="#fff" 
                tick={{ fontSize: 9 }} 
                width={50}
                tickCount={6}
                domain={[0, 'auto']}
                label={{ 
                  value: 'Mention Count', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: '#fff' }
                }}
              />
              <Tooltip />
              <Bar dataKey="value" fill="#8884d8">
                {results.keywords?.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.sentiment > 0.3 ? '#4ade80' : entry.sentiment < -0.3 ? '#f87171' : '#facc15'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
      
      {/* Sentiment by Platform */}
      <Card className="bg-secondary/50 border-2 border-primary">
        <CardHeader>
          <CardTitle className="text-white">Sentiment by Platform</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px]">
          <ChartContainer config={{
            sentiment: { color: '#8884d8' }
          }} className="w-full h-full">
            <BarChart 
              data={results.platforms} 
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
              barSize={15}
              barGap={2}
            >
              <XAxis dataKey="name" stroke="#fff" tick={{ fontSize: 9 }} />
              <YAxis 
                stroke="#fff" 
                tick={{ fontSize: 9 }} 
                width={50}
                tickCount={6}
                domain={[0, 'dataMax']}
                allowDecimals={false}
                label={{ 
                  value: 'Volume', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: '#fff' }
                }}
              />
              <Tooltip />
              <Bar dataKey="volume" name="Volume" fill="#8884d8">
                {results.platforms?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
      
      {/* Sentiment Timeline */}
      <Card className="bg-secondary/50 border-2 border-primary">
        <CardHeader>
          <CardTitle className="text-white">Sentiment Timeline</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px]">
          <ChartContainer config={{
            sentiment: { color: '#8884d8' }
          }} className="w-full h-full">
            <BarChart 
              data={results.timeline} 
              margin={{ top: 20, right: 30, left: 30, bottom: 20 }}
              barSize={15}
              barGap={2}
            >
              <XAxis dataKey="period" stroke="#fff" tick={{ fontSize: 9 }} />
              <YAxis 
                stroke="#fff" 
                tick={{ fontSize: 9 }} 
                width={50}
                tickCount={6}
                domain={[-1, 1]}
                allowDecimals={true}
                label={{ 
                  value: 'Sentiment Score', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { fill: '#fff' }
                }}
              />
              <Tooltip />
              <Bar dataKey="sentiment" name="Sentiment" fill="#8884d8">
                {results.timeline?.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={entry.sentiment > 0.3 ? '#4ade80' : entry.sentiment < -0.3 ? '#f87171' : '#facc15'} 
                  />
                ))}
              </Bar>
            </BarChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};
